"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader, ArrowRight } from "lucide-react";
const contactMethods = [{
  label: "ADMISSIONS OFFICE",
  value: "(416) 555-0123",
  href: "tel:+14165550123",
  note: "Mon\u2013Fri / 08:00\u201318:00 EST"
}, {
  label: "SCHOOL DIRECT",
  value: "hello@littleacorns.ca",
  href: "mailto:hello@littleacorns.ca",
  note: "We aim to reply within 24 business hours"
}, {
  label: "VISIT US",
  value: "Parkwoods-Donalda, North York",
  href: "https://maps.google.com",
  note: "Tours available by appointment only"
}];
function ConciergeForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="border border-border p-8 rounded-lg">
        <p className="font-default text-xs tracking-[0.2em] text-primary mb-4">
          MESSAGE RECEIVED
        </p>
        <p className="font-default text-2xl text-foreground mb-6 leading-snug">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="border-border bg-transparent text-foreground hover:bg-accent hover:text-accent-foreground font-default text-xs tracking-[0.2em] uppercase h-11 px-6 rounded-lg">
          Send Another
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="full_name" rules={{
      required: "Full name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
              Parent/Guardian Name
            </FormLabel>
            <FormControl>
              <Input placeholder="Jane Doe" {...field} className="border-0 border-b border-border bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-primary text-foreground placeholder:text-muted-foreground/60 font-default text-lg" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email_address" rules={{
      required: "Email address is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
              Email Address
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="jane@example.com" {...field} className="border-0 border-b border-border bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-primary text-foreground placeholder:text-muted-foreground/60 font-default text-lg" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="subject" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
              Inquiry Type
            </FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="border-0 border-b border-border bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-primary text-foreground font-default text-lg w-full data-[size=default]:h-11">
                  <SelectValue placeholder="Select an inquiry type" />
                </SelectTrigger>
                <SelectContent className="border-border bg-popover">
                  <SelectItem value="tour" className="font-default">
                    Book a School Tour
                  </SelectItem>
                  <SelectItem value="admissions" className="font-default">
                    Admissions & Enrollment
                  </SelectItem>
                  <SelectItem value="fees" className="font-default">
                    Fees & Subsidies
                  </SelectItem>
                  <SelectItem value="other" className="font-default">
                    General Inquiry
                  </SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Please provide more detail"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
              Message
            </FormLabel>
            <FormControl>
              <Textarea rows={4} placeholder="Tell us about your child and what you're looking for..." {...field} className="border-0 border-b border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary text-foreground placeholder:text-muted-foreground/60 font-default text-lg resize-none min-h-[7rem]" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="border border-destructive/40 p-4 rounded-lg">
          <p className="text-destructive font-default text-xs tracking-wider">
            {submitError}
          </p>
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="bg-transparent border-b border-primary text-primary hover:bg-transparent hover:text-primary/80 hover:border-primary/80 px-0 h-auto pb-2 font-default text-xs tracking-[0.25em] uppercase group">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-3.5" />
              Sending
            </> : <>
              Send Message
              <ArrowRight className="ml-3 size-3.5 transition-transform group-hover:translate-x-1" />
            </>}
        </Button>
      </div>

      <p className="font-default italic text-sm text-muted-foreground pt-2">
        We look forward to welcoming your family to our community.
      </p>
    </Form>;
}
export default function Contact() {
  return <section data-section-id="5051" id="reach-out-to-us" className="bg-background text-foreground pt-[200px] pb-32 px-6 md:px-12 lg:px-16">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-20 md:mb-28">
          <nav aria-label="breadcrumb">
            <ol className="flex items-center gap-2 font-default text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
              <li>
                <Link to="/HomePage" className="hover:text-foreground transition-colors">
                  HOME
                </Link>
              </li>
              <li aria-hidden="true">/</li>
              <li className="text-foreground">CONTACT</li>
            </ol>
          </nav>
          <span className="font-default text-[10px] tracking-[0.25em] uppercase text-primary">
            GET IN TOUCH
          </span>
        </div>

        <div className="grid grid-cols-12 gap-6 mb-20 md:mb-28">
          <h1 className="col-span-12 md:col-span-10 lg:col-span-8 font-default text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] leading-[1.05] font-semibold text-foreground tracking-tight">
            Start Your Child's <em className="italic font-medium">Journey</em>
          </h1>
          <p className="col-span-12 md:col-span-8 lg:col-span-6 md:col-start-1 font-default text-lg md:text-xl text-muted-foreground leading-relaxed font-normal">
            We are here to answer your questions about our Montessori programs, enrollment process, and community life in Parkwoods-Donalda.
          </p>
        </div>

        <div className="grid grid-cols-12 gap-x-6 gap-y-16">
          <div className="col-span-12 lg:col-span-6">
            <p className="font-default text-[10px] tracking-[0.25em] uppercase text-primary mb-10">
              CONTACT CHANNELS
            </p>
            <div className="flex flex-col">
              {contactMethods.map((method, i) => <div data-index={i} key={method.label} className="border-t border-border py-10 first:border-t last:border-b">
                  <p className="font-default text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-4">
                    {method.label}
                  </p>
                  <Link to={method.href} className="block font-default text-3xl md:text-4xl font-medium text-foreground hover:text-primary transition-colors leading-tight mb-3">
                    {method.value}
                  </Link>
                  <p className="font-default italic text-sm text-muted-foreground">
                    {method.note}
                  </p>
                </div>)}
            </div>
          </div>

          <div className="col-span-12 lg:col-span-6 lg:pl-12">
            <p className="font-default text-[10px] tracking-[0.25em] uppercase text-primary mb-6">
              ADMISSIONS INQUIRY
            </p>
            <p className="font-default text-lg text-muted-foreground leading-relaxed mb-12 font-normal">
              Use the form below to request a tour or ask about our current availability for the upcoming school year.
            </p>

            <FormProvider formKey="reach_out_form_contact_us" formId="reach_out_form_contact_us" sectionName="reach-out-to-us" defaultValues={{
            full_name: "",
            email_address: "",
            subject: "",
            message: ""
          }} validationRules={{
            full_name: {
              required: true,
              minLength: 2
            },
            email_address: {
              required: true,
              pattern: "email"
            },
            message: {
              required: true,
              minLength: 10
            }
          }} submitText="Send Message" successMessage="Thank you for reaching out. Our admissions team will be in touch shortly." errorMessage="Your message could not be sent. Please try again or contact us directly.">
              <ConciergeForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}